
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Check,
  AlertTriangle,
  RefreshCw,
  Copy,
  FileText,
  Sparkles,
  Upload,
  Download,
  Eye,
  Trash2,
} from "lucide-react";

interface DataCleaningToolsProps {
  onCleaningComplete: () => void;
}

interface CleaningIssue {
  type: 'duplicate' | 'missing' | 'format' | 'type' | 'outlier';
  description: string;
  count: number;
  severity: 'low' | 'medium' | 'high';
  auto_fixable: boolean;
}

interface CleaningResult {
  issues_detected: CleaningIssue[];
  cleaned_data: any[];
  original_count: number;
  cleaned_count: number;
  issues_fixed: string[];
}

export const DataCleaningTools = ({ onCleaningComplete }: DataCleaningToolsProps) => {
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rawData, setRawData] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [industry, setIndustry] = useState<string>("");
  const [cleaningResult, setCleaningResult] = useState<CleaningResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const { callAIService } = useOpenAIService();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setRawData(content);
        toast.success('File loaded successfully!');
      };
      reader.readAsText(uploadedFile);
      setFile(uploadedFile);
    }
  };

  const parseDataForCleaning = (data: string) => {
    try {
      const lines = data.split('\n').filter(line => line.trim());
      if (lines.length > 1) {
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        return lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });
      }
      return JSON.parse(data);
    } catch (e) {
      return [{ raw_data: data }];
    }
  };

  const detectDataIssues = (data: any[]): CleaningIssue[] => {
    const issues: CleaningIssue[] = [];
    
    if (data.length === 0) return issues;

    // Detect duplicates
    const duplicates = data.length - new Set(data.map(item => JSON.stringify(item))).size;
    if (duplicates > 0) {
      issues.push({
        type: 'duplicate',
        description: `${duplicates} duplicate records found`,
        count: duplicates,
        severity: 'medium',
        auto_fixable: true
      });
    }

    // Detect missing values
    const headers = Object.keys(data[0]);
    headers.forEach(header => {
      const missing = data.filter(row => !row[header] || row[header] === '').length;
      if (missing > data.length * 0.1) { // More than 10% missing
        issues.push({
          type: 'missing',
          description: `${missing} missing values in ${header}`,
          count: missing,
          severity: missing > data.length * 0.3 ? 'high' : 'medium',
          auto_fixable: true
        });
      }
    });

    // Detect format issues
    headers.forEach(header => {
      const values = data.map(row => row[header]).filter(v => v);
      const formatInconsistencies = detectFormatInconsistencies(values, header);
      if (formatInconsistencies > 0) {
        issues.push({
          type: 'format',
          description: `${formatInconsistencies} format inconsistencies in ${header}`,
          count: formatInconsistencies,
          severity: 'medium',
          auto_fixable: true
        });
      }
    });

    return issues;
  };

  const detectFormatInconsistencies = (values: any[], fieldName: string): number => {
    if (values.length === 0) return 0;
    
    // Check for mixed currency formats (₦5000 vs 5000)
    if (fieldName.toLowerCase().includes('price') || fieldName.toLowerCase().includes('amount')) {
      const withSymbol = values.filter(v => /[₦$£€]/.test(String(v))).length;
      const withoutSymbol = values.filter(v => /^\d+$/.test(String(v))).length;
      return Math.min(withSymbol, withoutSymbol);
    }
    
    // Check for mixed date formats
    if (fieldName.toLowerCase().includes('date')) {
      const formats = new Set(values.map(v => {
        const str = String(v);
        if (/\d{4}-\d{2}-\d{2}/.test(str)) return 'iso';
        if (/\d{2}\/\d{2}\/\d{4}/.test(str)) return 'us';
        if (/\d{2}-\d{2}-\d{4}/.test(str)) return 'eu';
        return 'other';
      }));
      return formats.size > 1 ? values.length - Math.max(...Array.from(formats).map(f => 
        values.filter(v => {
          const str = String(v);
          return f === 'iso' ? /\d{4}-\d{2}-\d{2}/.test(str) :
                 f === 'us' ? /\d{2}\/\d{2}\/\d{4}/.test(str) :
                 f === 'eu' ? /\d{2}-\d{2}-\d{4}/.test(str) : true;
        }).length
      )) : 0;
    }
    
    return 0;
  };

  const performIntelligentCleaning = (data: any[], issues: CleaningIssue[]) => {
    let cleaned = [...data];
    const issuesFixed: string[] = [];

    // Remove duplicates
    if (issues.some(i => i.type === 'duplicate')) {
      const originalLength = cleaned.length;
      const seen = new Set();
      cleaned = cleaned.filter(item => {
        const key = JSON.stringify(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      issuesFixed.push(`Removed ${originalLength - cleaned.length} duplicate records`);
    }

    // Fix format inconsistencies
    issues.filter(i => i.type === 'format').forEach(issue => {
      const fieldName = issue.description.split(' in ')[1];
      
      if (fieldName && (fieldName.toLowerCase().includes('price') || fieldName.toLowerCase().includes('amount'))) {
        cleaned = cleaned.map(row => {
          if (row[fieldName]) {
            // Standardize currency format - remove symbols and convert to number
            const cleaned_value = String(row[fieldName]).replace(/[₦$£€,]/g, '');
            if (!isNaN(Number(cleaned_value))) {
              row[fieldName] = Number(cleaned_value);
            }
          }
          return row;
        });
        issuesFixed.push(`Standardized currency format in ${fieldName}`);
      }
      
      if (fieldName && fieldName.toLowerCase().includes('date')) {
        cleaned = cleaned.map(row => {
          if (row[fieldName]) {
            const dateStr = String(row[fieldName]);
            // Try to convert to ISO format
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              row[fieldName] = date.toISOString().split('T')[0];
            }
          }
          return row;
        });
        issuesFixed.push(`Standardized date format in ${fieldName}`);
      }
    });

    // Handle missing values intelligently
    issues.filter(i => i.type === 'missing').forEach(issue => {
      const fieldName = issue.description.split(' in ')[1];
      if (fieldName) {
        const validValues = cleaned.map(row => row[fieldName]).filter(v => v && v !== '');
        if (validValues.length > 0) {
          // Use most common value for categorical data
          const valueCounts = validValues.reduce((acc: any, val) => {
            acc[val] = (acc[val] || 0) + 1;
            return acc;
          }, {});
          const mostCommon = Object.keys(valueCounts).reduce((a, b) => 
            valueCounts[a] > valueCounts[b] ? a : b
          );
          
          cleaned = cleaned.map(row => {
            if (!row[fieldName] || row[fieldName] === '') {
              row[fieldName] = mostCommon;
            }
            return row;
          });
          issuesFixed.push(`Filled missing values in ${fieldName} with most common value`);
        }
      }
    });

    return { cleaned, issuesFixed };
  };

  const handleIntelligentClean = async () => {
    if (!rawData.trim()) {
      toast.error("Please provide data to clean");
      return;
    }

    setIsProcessing(true);
    setProgress(10);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to use data cleaning');
        return;
      }

      const parsedData = parseDataForCleaning(rawData);
      setProgress(30);

      const detectedIssues = detectDataIssues(parsedData);
      setProgress(50);

      // Call AI service for advanced cleaning recommendations
      await callAIService({
        feature: 'advanced_data_cleaning',
        data: {
          raw_data: parsedData.slice(0, 100), // Limit sample for AI
          issues_detected: detectedIssues,
          industry: industry || 'general'
        },
        industry: industry || 'general',
        userId: user.id,
        interactionType: 'data_cleaning'
      });

      setProgress(70);

      // Perform intelligent cleaning
      const { cleaned, issuesFixed } = performIntelligentCleaning(parsedData, detectedIssues);
      setProgress(90);

      const result: CleaningResult = {
        issues_detected: detectedIssues,
        cleaned_data: cleaned,
        original_count: parsedData.length,
        cleaned_count: cleaned.length,
        issues_fixed: issuesFixed
      };

      setCleaningResult(result);
      setProgress(100);

      toast.success(`Cleaned data successfully! Fixed ${issuesFixed.length} types of issues.`);
      onCleaningComplete();
    } catch (error) {
      console.error("Error cleaning data:", error);
      toast.error("Failed to clean data. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadCleanedData = () => {
    if (!cleaningResult) return;

    const csvContent = convertToCSV(cleaningResult.cleaned_data);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `cleaned_data_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const convertToCSV = (data: any[]): string => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ];
    
    return csvRows.join('\n');
  };

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Intelligent Data Cleaning</h2>
          <p className="text-sm text-muted-foreground">
            AI-powered detection and fixing of data quality issues
          </p>
        </div>
        <Sparkles className="h-5 w-5 text-primary" />
      </div>
      
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Industry Context</label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger>
                <SelectValue placeholder="Select industry (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="ecommerce">E-commerce</SelectItem>
                <SelectItem value="logistics">Logistics</SelectItem>
                <SelectItem value="realestate">Real Estate</SelectItem>
                <SelectItem value="technology">Technology</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Upload Data File</label>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-3">
              <input
                type="file"
                accept=".csv,.json,.txt"
                onChange={handleFileUpload}
                className="hidden"
                id="cleaning-file-upload"
              />
              <label htmlFor="cleaning-file-upload" className="flex items-center gap-2 cursor-pointer">
                <Upload className="h-4 w-4" />
                <span className="text-sm">
                  {file ? file.name : 'Upload CSV/JSON/TXT'}
                </span>
              </label>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Or Paste Raw Data</label>
          <Textarea
            placeholder="Paste your CSV data, JSON, or any structured data here..."
            value={rawData}
            onChange={(e) => setRawData(e.target.value)}
            rows={4}
            className="resize-none"
          />
        </div>

        {isProcessing && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Intelligent Cleaning Progress</h3>
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground">{progress}% complete</p>
          </div>
        )}

        <Button
          onClick={handleIntelligentClean}
          disabled={isProcessing || !rawData.trim()}
          className="w-full"
          size="lg"
        >
          {isProcessing ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              AI Cleaning in Progress...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Clean Data with AI
            </>
          )}
        </Button>

        {cleaningResult && (
          <div className="mt-6 p-4 bg-muted rounded-lg space-y-4">
            <h4 className="font-medium">Cleaning Results</h4>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Original Records:</span>
                <div className="font-semibold">{cleaningResult.original_count}</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Cleaned Records:</span>
                <div className="font-semibold text-green-600">{cleaningResult.cleaned_count}</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Issues Fixed:</span>
                <div className="font-semibold">{cleaningResult.issues_fixed.length}</div>
              </div>
            </div>

            <div>
              <h5 className="text-sm font-medium mb-2">Issues Detected & Fixed:</h5>
              <div className="space-y-1">
                {cleaningResult.issues_fixed.map((issue, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <Check className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                    {issue}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setShowPreview(!showPreview)} variant="outline">
                <Eye className="mr-2 h-4 w-4" />
                {showPreview ? 'Hide Preview' : 'Preview Cleaned Data'}
              </Button>
              <Button onClick={downloadCleanedData}>
                <Download className="mr-2 h-4 w-4" />
                Download Cleaned CSV
              </Button>
            </div>

            {showPreview && cleaningResult.cleaned_data.length > 0 && (
              <div className="mt-4 border rounded-lg overflow-hidden">
                <div className="bg-muted px-3 py-2 border-b">
                  <span className="text-sm font-medium">Cleaned Data Preview (First 5 Rows)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        {Object.keys(cleaningResult.cleaned_data[0]).map((col, idx) => (
                          <th key={idx} className="px-3 py-2 text-left">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cleaningResult.cleaned_data.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="border-t">
                          {Object.keys(row).map((col, colIdx) => (
                            <td key={colIdx} className="px-3 py-2">
                              {String(row[col]).substring(0, 30)}
                              {String(row[col]).length > 30 ? '...' : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
